// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: matrice d'Eisenhower — classification pure des actions
// ═══════════════════════════════════════════════════════════════════════════
//
// Ornexia ne stocke PAS d'axes « urgence » / « importance » dédiés. La matrice
// les DÉRIVE des champs existants (décision produit juin 2026) :
//   - Urgence  ← échéance effective (deadline_revised sinon deadline).
//     URGENT si l'échéance est dans ≤ URGENCY_DAYS jours OU dépassée.
//     Sans échéance ⇒ NON urgent.
//   - Importance ← priorité (échelle 0 = très haute … 4 = très basse, défaut 2).
//     IMPORTANT si priorité ≤ IMPORTANCE_MAX.
//
// Les 4 quadrants Eisenhower :
//   do        : Important + Urgent      → « Faire maintenant »   (haut-droite)
//   plan      : Important + Non urgent  → « Planifier »          (haut-gauche)
//   delegate  : Non important + Urgent  → « Déléguer »           (bas-droite)
//   eliminate : Non important + Non urg.→ « Abandonner »         (bas-gauche)

export type EisenhowerQuadrant = 'do' | 'plan' | 'delegate' | 'eliminate';

export interface EisenhowerThresholds {
  /** Une échéance ≤ ce nombre de jours (ou dépassée) ⇒ urgente. */
  urgencyDays: number;
  /** Une priorité ≤ cette valeur ⇒ importante (0 = très haute … 4 = très basse). */
  importanceMax: number;
}

// Seuils validés (juin 2026) : urgence à 14 jours, importance ≤ 2.
export const DEFAULT_EISENHOWER_THRESHOLDS: EisenhowerThresholds = {
  urgencyDays: 14,
  importanceMax: 2,
};

export interface ClassifiableAction {
  priority?: number | null;
  deadline?: string | null;
  deadline_revised?: string | null;
}

/** Échéance retenue : la révisée prime sur l'initiale. */
export function effectiveDeadline(a: ClassifiableAction): string | null {
  return a.deadline_revised || a.deadline || null;
}

/**
 * Jours restants avant l'échéance effective (négatif = en retard), ou null si
 * aucune échéance. `today` au format 'YYYY-MM-DD' (injecté pour testabilité).
 */
export function remainingDays(a: ClassifiableAction, today: string): number | null {
  const dl = effectiveDeadline(a);
  if (!dl) return null;
  return Math.round((new Date(dl).getTime() - new Date(today).getTime()) / 86400000);
}

export function isUrgent(a: ClassifiableAction, today: string, urgencyDays: number): boolean {
  const rd = remainingDays(a, today);
  return rd != null && rd <= urgencyDays;
}

export function isImportant(a: ClassifiableAction, importanceMax: number): boolean {
  const prio = a.priority == null ? 2 : a.priority;
  return prio <= importanceMax;
}

export function classifyAction(
  a: ClassifiableAction,
  today: string,
  thresholds: EisenhowerThresholds = DEFAULT_EISENHOWER_THRESHOLDS,
): EisenhowerQuadrant {
  const urgent = isUrgent(a, today, thresholds.urgencyDays);
  const important = isImportant(a, thresholds.importanceMax);
  if (important && urgent) return 'do';
  if (important && !urgent) return 'plan';
  if (!important && urgent) return 'delegate';
  return 'eliminate';
}

/** Répartit les actions dans les 4 quadrants (ordre stable préservé). */
export function bucketByQuadrant<T extends ClassifiableAction>(
  actions: T[],
  today: string,
  thresholds: EisenhowerThresholds = DEFAULT_EISENHOWER_THRESHOLDS,
): Record<EisenhowerQuadrant, T[]> {
  const out: Record<EisenhowerQuadrant, T[]> = { do: [], plan: [], delegate: [], eliminate: [] };
  for (const a of actions) out[classifyAction(a, today, thresholds)].push(a);
  return out;
}
